import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { clamp } from 'lodash-es';
import { Range } from 'client/site-modules/lease-deals/components/filters/ui/range/range';
import { RangeEntity } from 'site-modules/lease-deals/components/filters/entities/options-entity';
import { LEASE_DEALS_FILTERS } from 'site-modules/lease-deals/constants/lease-deals-constants';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { RangeInputsPanel } from 'client/site-modules/lease-deals/components/filters/ui/range-inputs-panel/range-inputs-panel';
import { getNormalizedRange } from 'site-modules/lease-deals/components/filters/controls/price-range-control/utils';

export function PriceRangeControl({ options, dataKey, selectedValues, onChange }) {
  const [globalMin, globalMax] = options;
  const committedRange = useMemo(
    () => [
      clamp(selectedValues?.[0] ?? globalMin, globalMin, globalMax),
      clamp(selectedValues?.[1] ?? globalMax, globalMin, globalMax),
    ],
    [selectedValues, globalMin, globalMax]
  );
  const [draftRange, setDraftRange] = useState(null);
  const hasCurrentDraft = draftRange?.baseMin === committedRange[0] && draftRange?.baseMax === committedRange[1];
  const [minValue, maxValue] = hasCurrentDraft ? draftRange.values : committedRange;

  const fireTrackingAction = useCallback(
    (nextMin, nextMax) => {
      const hasFilter = !!((nextMin && nextMin !== globalMin) || (nextMax && nextMax !== globalMax));
      const trackingValue = hasFilter ? `${dataKey}~$${nextMin}-$${nextMax}` : dataKey;
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_TOOL_SELECT,
          subaction_name: hasFilter ? TrackingConstant.APPLY_FILTER : TrackingConstant.REMOVE_FILTER,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          creative_id: LEASE_DEALS_FILTERS,
          value: trackingValue,
        },
      });
    },
    [dataKey, globalMin, globalMax]
  );

  const handleUpdate = useCallback(
    (min, max) => {
      const currentMin = min ?? minValue;
      const currentMax = max ?? maxValue;

      const [nextMin, nextMax] = getNormalizedRange(currentMin, currentMax, globalMin, globalMax);

      const toNum = v => (v === '' ? null : Number(v));
      const convertedMin = toNum(nextMin);
      const convertedMax = toNum(nextMax);
      const newValues = [
        convertedMin === globalMin ? null : convertedMin,
        convertedMax === globalMax ? null : convertedMax,
      ];

      const committedMin = selectedValues?.[0] ?? globalMin;
      const committedMax = selectedValues?.[1] ?? globalMax;

      if ((newValues[0] ?? globalMin) === committedMin && (newValues[1] ?? globalMax) === committedMax) return;

      onChange(newValues);
      fireTrackingAction(newValues[0] ?? '', newValues[1] ?? '');
    },
    [onChange, fireTrackingAction, globalMin, globalMax, selectedValues, minValue, maxValue]
  );

  const handleMinChange = useCallback(
    (newMin, callUpdateAfter) => {
      const nextRange = [newMin, maxValue];
      setDraftRange({ baseMin: committedRange[0], baseMax: committedRange[1], values: nextRange });
      if (callUpdateAfter) handleUpdate(...nextRange);
    },
    [handleUpdate, maxValue, committedRange]
  );

  const handleMaxChange = useCallback(
    (newMax, callUpdateAfter) => {
      const nextRange = [minValue, newMax];
      setDraftRange({ baseMin: committedRange[0], baseMax: committedRange[1], values: nextRange });
      if (callUpdateAfter) handleUpdate(...nextRange);
    },
    [handleUpdate, minValue, committedRange]
  );

  const renderInputsPanel = useCallback(panelProps => {
    return (
      <RangeInputsPanel
        className="lease-deals-range-filter-inputs-panel lease-deals-range-filter-inputs-panel-price"
        {...panelProps}
      />
    );
  }, []);

  const startAdornment = useMemo(() => <span className="size-16 text-cool-gray-40 me-0_25">$</span>, []);

  return (
    <Range
      min={globalMin}
      max={globalMax}
      selectedMin={minValue}
      selectedMax={maxValue}
      hasTwoThumbs
      name="price-range"
      minOutputText={`${minValue}`}
      maxOutputText={`${maxValue}`}
      withInput
      allowEqualValues
      onMinChange={handleMinChange}
      onMaxChange={handleMaxChange}
      handleUpdate={handleUpdate}
      inputAddon={startAdornment}
      step={1}
      rangeGroupClassName={dataKey}
      renderInputsPanel={renderInputsPanel}
      showLabels
    />
  );
}

PriceRangeControl.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: RangeEntity.isRequired,
  dataKey: PropTypes.string.isRequired,
  selectedValues: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.arrayOf(PropTypes.string)]),
};
